import axios from 'axios';

const fromEnv = process.env.REACT_APP_API_URL || '';
const API_BASE = process.env.NODE_ENV === 'development'
  ? (fromEnv || '/api')
  : (fromEnv || 'https://api.growmoreitservices.com/api');

export const publicApi = axios.create({
  baseURL: API_BASE,
  timeout: 12000,
  headers: { 'Content-Type': 'application/json' }
});

export const adminApi = axios.create({
  baseURL: API_BASE,
  timeout: 12000,
  headers: { 'Content-Type': 'application/json' }
});

adminApi.interceptors.request.use((config) => {
  const isLogin = String(config.url || '').includes('/admin/login');
  const token = localStorage.getItem('admin_token');
  if (token && !isLogin) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  if (typeof FormData !== 'undefined' && config.data instanceof FormData) {
    delete config.headers['Content-Type'];
  }
  return config;
});

adminApi.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401 && window.location.pathname.startsWith('/admin')) {
      localStorage.removeItem('admin_token');
      localStorage.removeItem('admin_user');
      if (!window.location.pathname.startsWith('/admin/login')) {
        window.location.href = '/admin/login';
      }
    }
    return Promise.reject(error);
  }
);

export const internApi = axios.create({
  baseURL: API_BASE,
  timeout: 20000,
  headers: { 'Content-Type': 'application/json' }
});

internApi.interceptors.request.use((config) => {
  const token = localStorage.getItem('intern_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  if (typeof FormData !== 'undefined' && config.data instanceof FormData) {
    delete config.headers['Content-Type'];
  }
  return config;
});

internApi.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401 && window.location.pathname.startsWith('/intern/')) {
      localStorage.removeItem('intern_token');
      window.location.href = '/intern/login';
    }
    return Promise.reject(error);
  }
);

export const adminAuthAPI = {
  login: (credentials) => publicApi.post('/admin/login', credentials),
  logout: () => adminApi.post('/admin/logout'),
  me: () => adminApi.get('/admin/me')
};

export const adminEnquiriesAPI = {
  list: () => adminApi.get('/admin/enquiries'),
  updateStatus: (id, status) => adminApi.patch(`/admin/enquiries/${id}`, { status }),
  remove: (id) => adminApi.delete(`/admin/enquiries/${id}`)
};

export const partnersAPI = {
  list: () => publicApi.get('/partners')
};

export const adminPartnersAPI = {
  list: () => adminApi.get('/admin/partners'),
  create: (formData) => adminApi.post('/admin/partners', formData),
  update: (id, formData) => adminApi.post(`/admin/partners/${id}`, formData),
  toggle: (id) => adminApi.patch(`/admin/partners/${id}/visibility`),
  remove: (id) => adminApi.delete(`/admin/partners/${id}`)
};

export const internshipAPI = {
  catalog: () => publicApi.get('/internships'),
  formats: () => publicApi.get('/letters/formats'),
  apply: (payload) => publicApi.post('/internships/apply', payload),
  quoteCoupon: (payload) => publicApi.post('/internships/coupon', payload),
  pay: (id, payment_ref) => publicApi.post(`/internships/${id}/pay`, { payment_ref }),
  createOrder: (payload) => publicApi.post('/create-order', payload),
  verifyPayment: (payload) => publicApi.post('/verify-payment', payload),
  login: (payload) => publicApi.post('/intern/login', payload),
  forgot: (payload) => publicApi.post('/intern/forgot', payload),
  searchLetters: (q) => publicApi.get('/letters/search', { params: { q } }),
  letter: (code, unsigned = false) => publicApi.get(`/letters/${code}`, {
    params: unsigned ? { unsigned: 1 } : {},
    timeout: 30000
  })
};

export const internPortalAPI = {
  me: () => internApi.get('/intern/me'),
  switchInternship: (id) => internApi.post(`/intern/switch/${id}`),
  submitTask: (id, formData) => internApi.post(`/intern/tasks/${id}`, formData, { timeout: 60000 }),
  acknowledgeOffer: () => internApi.post('/intern/acknowledge-offer'),
  submitFinal: () => internApi.post('/intern/submit-final'),
  changePassword: (payload) => internApi.post('/intern/password', payload)
};

export const adminInternsAPI = {
  list: () => adminApi.get('/admin/interns'),
  show: (id) => adminApi.get(`/admin/interns/${id}`, { timeout: 60000 }),
  reviewTask: (internId, taskId) => adminApi.post(`/admin/interns/${internId}/tasks/${taskId}/review`),
  rejectTask: (internId, taskId) => adminApi.post(`/admin/interns/${internId}/tasks/${taskId}/reject`),
  previewOffline: (payload) => adminApi.post('/admin/interns/offline/preview', payload),
  markPaid: (id) => adminApi.post(`/admin/interns/${id}/pay`),
  resetPassword: (id) => adminApi.post(`/admin/interns/${id}/reset-password`),
  setPassword: (id, payload) => adminApi.post(`/admin/interns/${id}/password`, payload),
  impersonate: (id) => adminApi.post(`/admin/interns/${id}/login`),
  updateIntern: (id, payload) => adminApi.patch(`/admin/interns/${id}`, payload),
  createOffline: (payload) => adminApi.post('/admin/interns/offline', payload),
  setTask: (id, payload) => adminApi.post(`/admin/interns/${id}/task`, payload),
  approve: (id) => adminApi.post(`/admin/interns/${id}/approve`),
  issueLetter: (id, payload) => adminApi.post(`/admin/interns/${id}/letter`, payload),
  letter: (code, unsigned = true) => adminApi.get(`/admin/letters/${code}`, { params: { unsigned } }),
  updateLetter: (code, payload) => adminApi.patch(`/admin/letters/${code}`, payload),
  formats: () => adminApi.get('/admin/letter-formats'),
  updateFormat: (type, payload) => adminApi.patch(`/admin/letter-formats/${type}`, payload),
  plans: () => adminApi.get('/admin/internship-plans'),
  createPlan: (payload) => adminApi.post('/admin/internship-plans', payload),
  updatePlan: (id, payload) => adminApi.patch(`/admin/internship-plans/${id}`, payload),
  deletePlan: (id) => adminApi.delete(`/admin/internship-plans/${id}`),
  coupons: () => adminApi.get('/admin/intern-coupons'),
  createCoupon: (payload) => adminApi.post('/admin/intern-coupons', payload),
  updateCoupon: (id, payload) => adminApi.patch(`/admin/intern-coupons/${id}`, payload),
  deleteCoupon: (id) => adminApi.delete(`/admin/intern-coupons/${id}`)
};

export const adminDocumentsAPI = {
  list: (type) => adminApi.get('/admin/documents', { params: type ? { type } : {} }),
  create: (payload) => adminApi.post('/admin/documents', payload),
  update: (id, payload) => adminApi.patch(`/admin/documents/${id}`, payload),
  remove: (id) => adminApi.delete(`/admin/documents/${id}`)
};

export default publicApi;
